import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Building2, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RuneLogo } from "@/components/RuneLogo";
import { FullScreenLoader } from "@/components/Loader";
import { PRIMARY } from "@/components/landing/shared";

// OAuth 2.1 consent page (Phase 12) — the authorization_endpoint the Claude apps open when a user
// adds the Runebox connector. The user is (or gets) logged into runebox.ai, approves once, and we
// POST /api/oauth/authorize to mint the PKCE-bound code, then redirect back to the client.
// All values are rendered as plain JSX text (React escapes) — no HTML injection surface.
export default function Authorize() {
  const { user, org, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const q = {
    client_id: params.get("client_id"),
    redirect_uri: params.get("redirect_uri"),
    code_challenge: params.get("code_challenge"),
    code_challenge_method: params.get("code_challenge_method") || "S256",
    state: params.get("state"),
    scope: params.get("scope"),
    resource: params.get("resource"),
    response_type: params.get("response_type"),
  };
  const invalid = !q.client_id || !q.redirect_uri || !q.code_challenge || q.response_type !== "code";

  const targetHost = useMemo(() => {
    try { return new URL(q.redirect_uri).host; } catch { return q.redirect_uri || "the application"; }
  }, [q.redirect_uri]);

  // Not logged in → send to login, returning right back here to approve.
  useEffect(() => {
    if (!loading && !user) {
      const here = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(here)}`, { replace: true });
    }
  }, [loading, user, navigate]);

  const allow = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/oauth/authorize", q);
      window.location.href = data.redirect;
    } catch (e) {
      setError(apiErr(e, "Couldn't authorize the connection"));
      setSubmitting(false);
    }
  };

  const deny = () => {
    if (!q.redirect_uri) return navigate("/");
    const sep = q.redirect_uri.includes("?") ? "&" : "?";
    let url = `${q.redirect_uri}${sep}error=access_denied`;
    if (q.state) url += `&state=${encodeURIComponent(q.state)}`;
    window.location.href = url;
  };

  if (loading || !user) return <FullScreenLoader />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--rb-void)] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-8 shadow-[0_24px_70px_rgba(0,0,0,0.5)]" data-testid="oauth-consent">
        <div className="flex items-center gap-2.5">
          <RuneLogo className="h-8 w-8" />
          <span className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">Runebox</span>
        </div>

        {invalid ? (
          <>
            <h1 className="rb-display mt-6 text-xl font-semibold text-[color:var(--rb-text)]">Invalid connection request</h1>
            <p className="mt-2 text-sm text-[color:var(--rb-text-dim)]">This authorization link is missing required parameters. Start the connection again from Claude&rsquo;s connector settings.</p>
            <Button onClick={() => navigate("/connect")} className={`mt-6 h-11 w-full rounded-xl ${PRIMARY}`}>How to connect</Button>
          </>
        ) : (
          <>
            <h1 className="rb-display mt-6 text-xl font-semibold text-[color:var(--rb-text)]">Connect to the Claude apps</h1>
            <p className="mt-2 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">
              Allow this connection to <b className="text-[color:var(--rb-text)]">read your approved Runebox catalog</b> — your skills, agents, and commands — so they show up in Claude on the web and desktop. It can&rsquo;t change or publish anything.
            </p>

            <div className="mt-5 space-y-2 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-4 text-sm">
              <div className="flex items-center gap-2 text-[color:var(--rb-text-dim)]">
                <UserIcon className="h-4 w-4 text-[color:var(--rb-accent)]" /> Signed in as <span className="text-[color:var(--rb-text)]">@{user.username}</span>
              </div>
              {org && (
                <div className="flex items-center gap-2 text-[color:var(--rb-text-dim)]">
                  <Building2 className="h-4 w-4 text-[color:var(--rb-accent)]" /> Catalog from <span className="text-[color:var(--rb-text)]">{org.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-[color:var(--rb-text-faint)]">
                <ShieldCheck className="h-4 w-4" /> Redirects to <span className="rb-mono text-[color:var(--rb-text-dim)]">{targetHost}</span>
              </div>
            </div>

            {error && <p className="mt-3 text-sm text-[color:var(--rb-danger,#f87171)]" data-testid="oauth-error">{error}</p>}

            <div className="mt-6 flex gap-3">
              <Button onClick={deny} variant="outline" disabled={submitting} data-testid="oauth-deny"
                className="h-11 flex-1 rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:bg-[color:var(--rb-surface)]">
                Deny
              </Button>
              <Button onClick={allow} disabled={submitting} data-testid="oauth-allow"
                className={`h-11 flex-1 rounded-xl ${PRIMARY}`}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Allow"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
